import {
	type BrandIdentity,
	type BrandRuntime,
	type BrandRuntimeArtifact,
	BrandRuntimeErrorCode,
	type DesignConfiguration,
	type InvalidBrandRuntimeArtifact,
	type ResolvedTheme,
	type ValidBrandRuntimeArtifact,
} from '@today/brand-config/runtime'
import { createContext, type ReactNode, useContext, useInsertionEffect, useLayoutEffect, useMemo } from 'react'
import { BrandFallback, BrandRuntimeError } from './brand-runtime-error'

/**
 * Props for {@link BrandProvider}.
 *
 * @property brand - A compiled brand from `virtual:today-brands` (`@today/brand-config/vite`).
 * @property applyToDocument - Also bind `<html data-brand>` while mounted, so overlays portalled to
 *   `document.body` are themed. Used once per page, by the application root or the Storybook decorator.
 * @property fallback - What an invalid artifact does: throw (default) or show the diagnostics panel.
 */
export interface BrandProviderProps {
	brand: BrandRuntimeArtifact
	applyToDocument?: boolean
	fallback?: BrandFallback
	children?: ReactNode
}

const BrandContext = createContext<BrandRuntime | null>(null)

/** Ensures `<head>` holds exactly one `<style data-brand-theme="<id>">` with the brand's CSS. */
function useThemeStyle(id: string, css: string): void {
	useInsertionEffect(() => {
		const existing = [...document.head.querySelectorAll<HTMLStyleElement>('style[data-brand-theme]')].find(
			(candidate) => candidate.getAttribute('data-brand-theme') === id,
		)
		const style = existing ?? document.createElement('style')
		if (!existing) {
			style.setAttribute('data-brand-theme', id)
			document.head.appendChild(style)
		}
		// textContent only, never innerHTML: the CSS is data, not markup.
		if (style.textContent !== css) style.textContent = css
	}, [id, css])
}

/** Binds `<html data-brand>` while mounted and restores the previous value on cleanup. */
function useDocumentBinding(id: string, enabled: boolean): void {
	useLayoutEffect(() => {
		if (!enabled) return undefined
		const root = document.documentElement
		const previous = root.getAttribute('data-brand')
		root.setAttribute('data-brand', id)
		return () => {
			if (previous === null) root.removeAttribute('data-brand')
			else root.setAttribute('data-brand', previous)
		}
	}, [id, enabled])
}

function ValidBrand({
	brand,
	applyToDocument = false,
	children,
}: {
	brand: ValidBrandRuntimeArtifact
	applyToDocument?: boolean
	children?: ReactNode
}) {
	useThemeStyle(brand.id, brand.themeCss)
	useDocumentBinding(brand.id, applyToDocument)
	const runtime = useMemo<BrandRuntime>(
		() => ({
			brand: brand.definition,
			identity: brand.definition.identity,
			theme: { css: brand.themeCss, tokens: brand.tokens },
			design: brand.definition.design,
		}),
		[brand],
	)
	return (
		<BrandContext.Provider value={runtime}>
			<div data-brand={brand.id}>{children}</div>
		</BrandContext.Provider>
	)
}

/** The diagnostics panel shown for an invalid brand: data only, no embedded copy. */
function BrandDiagnostics({ brand }: { brand: InvalidBrandRuntimeArtifact }) {
	return (
		<div role="alert" data-brand-diagnostics={brand.id} className="p-4 font-ds-mono text-ds-sm">
			{brand.diagnostics.map((line, index) => (
				// Diagnostic lines may repeat; their position is their identity.
				// biome-ignore lint/suspicious/noArrayIndexKey: ordered, static list
				<pre key={index} className="whitespace-pre-wrap">
					{line}
				</pre>
			))}
		</div>
	)
}

/**
 * Binds a compiled brand to its subtree (brand-runtime, ADR-0045, tech spec §17–18).
 *
 * @remarks
 * - Renders a wrapper `<div data-brand="<id>">`; `ds-*` utilities inside it resolve to that
 *   brand's `--ds-*` variables, so providers nest and several brands can share one page.
 * - Injects the brand's `theme.css` once per brand id into `<head>`.
 * - Exposes {@link BrandRuntime} through {@link useBrand} and the narrower hooks: semantic
 *   configuration only — reusable UI never branches on the brand id.
 * - An invalid artifact throws `BRAND_ARTIFACT_INVALID`, or renders the diagnostics panel with
 *   `fallback={BrandFallback.Diagnostics}` (development surfaces, tech spec §69).
 *
 * @example
 * ```tsx
 * import { brands } from 'virtual:today-brands'
 *
 * <BrandProvider brand={brands[0]} applyToDocument>
 *   <App />
 * </BrandProvider>
 * ```
 */
export function BrandProvider({
	brand,
	applyToDocument,
	fallback = BrandFallback.Throw,
	children,
}: BrandProviderProps) {
	if (brand.valid) {
		return (
			<ValidBrand brand={brand} applyToDocument={applyToDocument}>
				{children}
			</ValidBrand>
		)
	}
	if (fallback === BrandFallback.Diagnostics) return <BrandDiagnostics brand={brand} />
	throw new BrandRuntimeError(
		BrandRuntimeErrorCode.ArtifactInvalid,
		[`Brand "${brand.id}" failed to compile:`, ...brand.diagnostics].join('\n'),
	)
}

/** The whole {@link BrandRuntime} of the nearest {@link BrandProvider}. Throws `BRAND_PROVIDER_MISSING` outside one. */
export function useBrand(): BrandRuntime {
	const runtime = useContext(BrandContext)
	if (runtime === null) {
		throw new BrandRuntimeError(BrandRuntimeErrorCode.ProviderMissing, 'Brand hooks must run inside a <BrandProvider>')
	}
	return runtime
}

/** The bound brand's identity (name, logo and other marks, fonts). */
export function useBrandIdentity(): BrandIdentity {
	return useBrand().identity
}

/** The bound brand's resolved theme (`theme.css` and the flat token values). */
export function useTheme(): ResolvedTheme {
	return useBrand().theme
}

/** The bound brand's design configuration (component, pattern and layout selections). */
export function useDesign(): DesignConfiguration {
	return useBrand().design
}

/**
 * The bound brand's design configuration, or `null` outside any {@link BrandProvider}. For
 * component variants that must also render without a provider (they fall back to the slot
 * default); configured patterns and layouts use {@link useConfiguredCapability} instead.
 */
export function useOptionalDesign(): DesignConfiguration | null {
	return useContext(BrandContext)?.design ?? null
}
